
import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptions } from '@/services/subscriptionService';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/hooks/useLanguage';

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-card p-3 shadow-md border border-border">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={`tooltip-${index}`} className="flex items-center space-x-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
            <span className="font-medium">{entry.name}:</span>
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

interface SubscriptionChartProps {
  className?: string;
}

const SubscriptionChart: React.FC<SubscriptionChartProps> = ({ className }) => {
  const { translate } = useLanguage();
  const { data: subscriptions = [], isLoading } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: getSubscriptions
  });

  const chartData = useMemo(() => {
    if (!subscriptions.length) return [];

    // Get the past 12 months
    const months = [];
    const today = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({
        month: d.toLocaleString('default', { month: 'short' }),
        year: d.getFullYear(),
        monthIndex: d.getMonth(),
        active: 0,
        registered: 0,
        terminated: 0
      });
    }

    // Process subscription data for each month
    subscriptions.forEach(subscription => {
      const regDate = new Date(subscription.registered_at);
      const activationDate = subscription.activation_date ? new Date(subscription.activation_date) : null;
      const terminationDate = subscription.termination_date ? new Date(subscription.termination_date) : null;

      months.forEach(monthData => {
        const monthStart = new Date(monthData.year, monthData.monthIndex, 1);
        const monthEnd = new Date(monthData.year, monthData.monthIndex + 1, 0);

        // Count registered in this month
        if (regDate >= monthStart && regDate <= monthEnd) {
          monthData.registered++;
        }

        // Count terminated in this month
        if (terminationDate && terminationDate >= monthStart && terminationDate <= monthEnd) {
          monthData.terminated++;
        }

        // Count active at the end of month (registered before or during month and not terminated or terminated after month)
        if (regDate <= monthEnd && 
           (!terminationDate || terminationDate > monthEnd) && 
           (activationDate === null || activationDate <= monthEnd)) {
          monthData.active++;
        }
      });
    });

    return months;
  }, [subscriptions]);

  if (isLoading) {
    return (
      <div className={cn("glass-card p-6", className)}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">{translate('dashboard.subscriptionTrends')}</h3>
          <p className="text-sm text-muted-foreground">{translate('dashboard.monthlyActivity')}</p>
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  return (
    <div className={cn("glass-card p-6", className)}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold">{translate('dashboard.subscriptionTrends')}</h3>
        <p className="text-sm text-muted-foreground">{translate('dashboard.monthlyActivity')}</p>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4ade80" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorRegistered" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorTerminated" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f87171" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }} 
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis 
              tick={{ fontSize: 12 }} 
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              iconType="circle" 
              iconSize={8}
              wrapperStyle={{ paddingTop: 15 }}
            />
            <Area 
              type="monotone" 
              dataKey="active" 
              name={translate('subscriptions.statuses.active')}
              stroke="#4ade80" 
              fill="url(#colorActive)" 
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
            <Area 
              type="monotone" 
              dataKey="registered" 
              name={translate('subscriptions.statuses.registered')}
              stroke="#60a5fa" 
              fill="url(#colorRegistered)" 
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
            <Area 
              type="monotone" 
              dataKey="terminated" 
              name={translate('subscriptions.statuses.terminated')}
              stroke="#f87171" 
              fill="url(#colorTerminated)"
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SubscriptionChart;
